// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useColumnSelection } from "@/hooks/use-column-selection";
import { useTrackedSessionStorage } from "@/hooks/use-tracked-browser-storage";
import { buildFilterGroupFromColumnState } from "@/lib/filters";
import { toPluralEntityType } from "@/lib/select-entity-type";
import {
  computeReportDesiredColumnIds,
  ensureReportColumnsSelected,
} from "@/pages/deep-research/report/drr-table-common";
import type { DeepResearchReportMetadata } from "@/types/deep-research-reports";
import type { UnifiedTableColumn } from "@/types/table-column";

/**
 * Hook for managing isolated state (filters, columns) for a specific DRR report.
 * This ensures that each report has its own state that doesn't affect other reports
 * or the main entity tables.
 *
 * Uses report-specific storage namespaces to ensure 100% isolation.
 */
export function useDRRReportState<T = Record<string, unknown>>(
  report: DeepResearchReportMetadata,
  entityType: "opportunity" | "account",
  coreColumns?: UnifiedTableColumn<T>[],
) {
  // Create report-specific storage namespace to ensure complete isolation
  const storageNamespace = `drr::${report.id}::${entityType}`;

  // Use our generic column selection hook with report-specific storage key
  const columnState = useColumnSelection<T>(
    toPluralEntityType(entityType),
    coreColumns || [],
    storageNamespace,
  );

  // Storage key for report-specific filter state
  const filterStorageKey = `${storageNamespace}::filters`;
  const [reportFilters, setReportFilters] = useTrackedSessionStorage<
    Record<string, unknown>
  >(filterStorageKey, {});

  // Track the last known AI field IDs to detect when new ones are added
  const lastKnownAIFieldIdsRef = useRef<string>("");

  // Auto-include desired columns from the report (AI + filtered fields) on mount
  useEffect(() => {
    // Skip if report hasn't loaded yet
    if (!report?.id) return;

    // Skip if field definitions are still loading
    // This is critical: we can't select columns until field definitions are available
    if (columnState.isLoading) return;

    // Get current AI field IDs from the report
    const currentAIFieldIds = (report.ai_fields || [])
      .map((f) => f.id)
      .sort()
      .join(",");

    // Determine if we should include AI fields:
    // 1. On initial mount (lastKnownAIFieldIdsRef is empty)
    // 2. When new AI fields are added (more fields than before)
    const shouldIncludeAI =
      lastKnownAIFieldIdsRef.current === "" ||
      currentAIFieldIds.split(",").filter(Boolean).length >
        lastKnownAIFieldIdsRef.current.split(",").filter(Boolean).length;

    // Update our tracking
    lastKnownAIFieldIdsRef.current = currentAIFieldIds;

    ensureReportColumnsSelected(
      report,
      reportFilters,
      reportFilters,
      {
        isColumnSelected: columnState.isColumnSelected,
        toggleColumn: columnState.toggleColumn,
      },
      { includeAI: shouldIncludeAI },
    );
    // Dependencies:
    // - report.id to ensure we have a valid report
    // - JSON.stringify(report.ai_fields) to detect when new AI fields are added
    // - columnState.isLoading to wait for field definitions
    // - columnState functions for latest handlers
  }, [
    report?.id,
    columnState.isLoading,
    columnState.isColumnSelected,
    columnState.toggleColumn,
    report.ai_fields,
    reportFilters,
    report,
  ]);

  // Build query params that include all selected columns and AI fields
  const getQueryParams = useCallback(
    (options: { searchTerm?: string; columns?: unknown[]; limit?: number }) => {
      // Build query params directly from report-specific filters
      const { searchTerm = "", columns = [], limit = 200 } = options;

      // Default sort settings based on entity type (align with list defaults)
      const DEFAULT_SORT_COLUMN =
        entityType === "opportunity" ? "close_date" : "opportunity_count";
      const DEFAULT_SORT_DIRECTION = "desc";

      // Get sort values from reportFilters
      const sortCol =
        (reportFilters.sortColumn as string) || DEFAULT_SORT_COLUMN;
      const sortDir =
        (reportFilters.sortDirection as string) || DEFAULT_SORT_DIRECTION;

      // Find column definition for the current sort to get nullSort
      const column = (columns as UnifiedTableColumn<unknown>[]).find(
        (col) => col.key === sortCol,
      );

      // Build base params
      const baseParams: Record<string, unknown> = {
        sort_by: sortCol,
        sort_direction: sortDir === null ? undefined : sortDir,
        null_sort: column?.nullSort,
        search: searchTerm || (reportFilters.search as string) || undefined,
        limit,
      };

      // Build filter group from report filters
      const validColumnKeys = new Set(
        (columns as UnifiedTableColumn<unknown>[]).map((c) => c.key.toString()),
      );
      const filterGroup = buildFilterGroupFromColumnState(
        reportFilters,
        validColumnKeys,
      );
      if (filterGroup) {
        baseParams.filters = JSON.stringify(filterGroup);
      }

      // Build complete field list from column state
      const extraFields: string[] = [];

      // Add selected custom fields
      if (columnState.customFieldIdsParam) {
        extraFields.push(...columnState.customFieldIdsParam.split(","));
      }

      // Add AI field IDs
      if (report.ai_fields && report.ai_fields.length > 0) {
        const aiFieldIds = report.ai_fields.map((field) => field.id);
        extraFields.push(...aiFieldIds);
      }

      // Add any fields from column visibility that aren't core columns
      Object.entries(columnState.columnVisibility).forEach(
        ([fieldId, isVisible]) => {
          if (
            isVisible &&
            !columnState.selectedCoreColumns.some((col) => col.id === fieldId)
          ) {
            extraFields.push(fieldId);
          }
        },
      );

      // Include any filtered field keys to guarantee data is loaded for filtered columns
      const filterKeys = Object.keys(reportFilters || {}).filter(
        (k) => k !== "sortColumn" && k !== "sortDirection" && k !== "search",
      );
      filterKeys.forEach((k) => extraFields.push(k));

      // Deduplicate and set
      const uniqueFields = [...new Set(extraFields)];
      if (uniqueFields.length > 0) {
        baseParams.extra_fields = uniqueFields.join(",");
      }

      return baseParams;
    },
    [reportFilters, report.ai_fields, columnState, entityType],
  );

  // Report-specific filter management
  const updateFilter = useCallback(
    (key: string, value: unknown) => {
      setReportFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [setReportFilters],
  );

  const clearFilter = useCallback(
    (key: string) => {
      setReportFilters((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    },
    [setReportFilters],
  );

  const clearAllFilters = useCallback(() => {
    setReportFilters({});
  }, [setReportFilters]);

  const updateFilters = useCallback(
    (newFilters: Record<string, unknown>) => {
      setReportFilters(newFilters);
    },
    [setReportFilters],
  );

  // Create the state override object with ALL required functions
  const stateOverride = useMemo(
    () => ({
      columns: {
        // State
        columnVisibility: columnState.columnVisibility,
        selectedCustomColumns: columnState.selectedCustomColumns,
        customFieldIdsParam: columnState.customFieldIdsParam,
        availableColumns: columnState.availableColumns,
        selectedColumns: columnState.selectedColumns,
        selectedCoreColumns: columnState.selectedCoreColumns,

        // Functions
        setColumnVisibility: columnState.setColumnVisibility,
        toggleColumn: columnState.toggleColumn,
        isColumnSelected: columnState.isColumnSelected,
        clearSelection: columnState.clearSelection,
        resetToDefault: () => {
          // Reset core columns to their defaults
          columnState.resetToDefault();
          // Determine the exact desired custom selection (AI + filtered)
          const desiredIds = computeReportDesiredColumnIds(
            report,
            reportFilters,
            reportFilters,
            { includeAI: true },
          );
          // Replace selection idempotently (no toggling)
          columnState.replaceCustomSelection?.(desiredIds);
        },
        getVisibleColumnDefs: columnState.getVisibleColumnDefs,

        // Loading/error state
        isLoading: columnState.isLoading,
        error: columnState.error,
      },
      filters: {
        activeFilters: reportFilters,
        updateFilter,
        clearFilter,
        clearAllFilters,
      },
      getQueryParams,
    }),
    [
      columnState.columnVisibility,
      columnState.setColumnVisibility,
      columnState.selectedCustomColumns,
      columnState.customFieldIdsParam,
      columnState.availableColumns,
      columnState.selectedColumns,
      columnState.selectedCoreColumns,
      columnState.toggleColumn,
      columnState.isColumnSelected,
      columnState.clearSelection,
      columnState.resetToDefault,
      columnState.getVisibleColumnDefs,
      columnState.isLoading,
      columnState.error,
      reportFilters,
      updateFilter,
      clearFilter,
      clearAllFilters,
      getQueryParams,
      report, // Replace selection idempotently (no toggling)
      columnState.replaceCustomSelection,
    ],
  );

  // Helper function to build query params with custom filters
  const getQueryParamsWithFilters = useCallback(
    (
      customFilters: Record<string, unknown>,
      options: { searchTerm?: string; columns?: unknown[]; limit?: number },
    ) => {
      // Build query params directly from provided filters
      const { searchTerm = "", columns = [], limit = 200 } = options;

      // Default sort settings based on entity type (align with list defaults)
      const DEFAULT_SORT_COLUMN =
        entityType === "opportunity" ? "close_date" : "opportunity_count";
      const DEFAULT_SORT_DIRECTION = "desc";

      // Get sort values from customFilters
      const sortCol =
        (customFilters.sortColumn as string) || DEFAULT_SORT_COLUMN;
      const sortDir =
        (customFilters.sortDirection as string) || DEFAULT_SORT_DIRECTION;

      // Find column definition for the current sort to get nullSort
      const column = (columns as UnifiedTableColumn<unknown>[]).find(
        (col) => col.key === sortCol,
      );

      // Build base params
      const baseParams: Record<string, unknown> = {
        sort_by: sortCol,
        sort_direction: sortDir === null ? undefined : sortDir,
        null_sort: column?.nullSort,
        search: searchTerm || (customFilters.search as string) || undefined,
        limit,
      };

      // Build filter group from custom filters
      const validColumnKeys = new Set(
        (columns as UnifiedTableColumn<unknown>[]).map((c) => c.key.toString()),
      );
      const filterGroup = buildFilterGroupFromColumnState(
        customFilters,
        validColumnKeys,
      );
      if (filterGroup) {
        baseParams.filters = JSON.stringify(filterGroup);
      }

      // Build complete field list from column state
      const extraFields: string[] = [];

      // Add selected custom fields
      if (columnState.customFieldIdsParam) {
        extraFields.push(...columnState.customFieldIdsParam.split(","));
      }

      // Add AI field IDs
      if (report.ai_fields && report.ai_fields.length > 0) {
        const aiFieldIds = report.ai_fields.map((field) => field.id);
        extraFields.push(...aiFieldIds);
      }

      // Add any fields from column visibility that aren't core columns
      Object.entries(columnState.columnVisibility).forEach(
        ([fieldId, isVisible]) => {
          if (
            isVisible &&
            !columnState.selectedCoreColumns.some((col) => col.id === fieldId)
          ) {
            extraFields.push(fieldId);
          }
        },
      );

      // Include any filtered field keys to guarantee data is loaded for filtered columns
      const filterKeys = Object.keys(customFilters || {}).filter(
        (k) => k !== "sortColumn" && k !== "sortDirection" && k !== "search",
      );
      filterKeys.forEach((k) => extraFields.push(k));

      // Deduplicate and set
      const uniqueFields = [...new Set(extraFields)];
      if (uniqueFields.length > 0) {
        baseParams.extra_fields = uniqueFields.join(",");
      }

      return baseParams;
    },
    [report.ai_fields, columnState, entityType],
  );

  return {
    // Complete state override for tables
    stateOverride,

    // Individual state access (for backward compatibility)
    filters: reportFilters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters: Object.keys(reportFilters).length > 0,
    getQueryParams,
    getQueryParamsWithFilters,
    updateFilters,

    // Column state
    columnVisibility: columnState.columnVisibility,
    setColumnVisibility: columnState.setColumnVisibility,
    selectedColumns: columnState.selectedColumns,
    customFieldIdsParam: columnState.customFieldIdsParam,
  };
}
